import { useEffect, useMemo, useState } from 'react'
import { PermissionAction, SecurityRole } from '../constants'
import { useAuth } from '../contexts/AuthContext'
import { ApiError } from '../services/BaseApiService'
import { rolesApiService, type Role, type RoleMember, type FolderGrant, type CapabilityGrant, TEAM_CAPABILITIES } from '../services/rolesApiService'
import { settingsApiService } from '../services/settingsApiService'
import { foldersApiService } from '../services/foldersApiService'
import type { Folder, TeamMember } from '../types'
import { colors, fontSize, radius, spacing, tracking } from '../styles/theme'
import { Button, Input, Select, SectionCard, ConfirmModal, useDialogs } from '../components/ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUserGroup, faFolderTree, faTrash, faPlus, faXmark } from '@fortawesome/free-solid-svg-icons'

const ACTION_LABELS: Record<number, string> = {
  [PermissionAction.VIEW]: 'View',
  [PermissionAction.RENDER]: 'Render',
  [PermissionAction.EDIT]: 'Edit',
  [PermissionAction.MANAGE]: 'Manage',
}
const ACTION_OPTIONS = [
  { value: PermissionAction.VIEW, label: 'View — see templates and render jobs' },
  { value: PermissionAction.RENDER, label: 'Render — run renders' },
  { value: PermissionAction.EDIT, label: 'Edit — create and modify templates' },
  { value: PermissionAction.MANAGE, label: 'Manage — grant permissions on the folder' },
]

const memberName = (m: { firstName: string | null; lastName: string | null; emailAddress: string }) =>
  [m.firstName, m.lastName].filter(Boolean).join(' ') || m.emailAddress

export default function RolesPermissionsPage() {
  const dialogs = useDialogs()
  const { user: me } = useAuth()
  const isAdmin = (me?.securityRole ?? 0) >= SecurityRole.TEAM_ADMIN

  const [roles, setRoles] = useState<Role[]>([])
  const [caps, setCaps] = useState<CapabilityGrant[]>([])
  const [teamUsers, setTeamUsers] = useState<TeamMember[]>([])
  const [folders, setFolders] = useState<Folder[]>([])
  const [error, setError] = useState('')

  const [newRoleName, setNewRoleName] = useState('')
  const [creating, setCreating] = useState(false)
  const [expandedRole, setExpandedRole] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null)

  async function loadRoles() {
    try { setRoles(await rolesApiService.list()) }
    catch (err) { setError(err instanceof ApiError ? err.message : 'Failed to load roles') }
  }

  async function loadCaps() {
    try { setCaps(await rolesApiService.capabilities()) } catch { /* non-fatal */ }
  }

  useEffect(() => {
    if (!isAdmin) return
    loadRoles()
    loadCaps()
    settingsApiService.listTeamUsers().then(setTeamUsers).catch(() => {})
    foldersApiService.listAll().then(setFolders).catch(() => {})
  }, [isAdmin])

  async function toggleCapability(roleId: string, code: string, has: boolean) {
    try {
      if (has) await rolesApiService.revokeCapability(roleId, code)
      else await rolesApiService.grantCapability(roleId, code)
      await loadCaps()
    } catch (err) { setError(err instanceof ApiError ? err.message : 'Failed to update capability') }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    const name = newRoleName.trim()
    if (!name) return
    setCreating(true); setError('')
    try {
      await rolesApiService.create(name)
      setNewRoleName('')
      await loadRoles()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create role')
    } finally {
      setCreating(false)
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    const target = deleteTarget
    setDeleteTarget(null)
    try {
      await rolesApiService.delete(target.roleId)
      if (expandedRole === target.roleId) setExpandedRole(null)
      await loadRoles()
    } catch (err) {
      void dialogs.alert(err instanceof ApiError ? err.message : 'Failed to delete role')
    }
  }

  if (!isAdmin) {
    return (
      <div>
        <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: colors.text, margin: 0, letterSpacing: tracking.tight }}>
          Roles &amp; Permissions
        </h1>
        <p style={{ color: colors.textSecondary, marginTop: spacing.md }}>
          Team admin access is required to manage roles and folder permissions.
        </p>
      </div>
    )
  }

  return (
    <>
    <div style={{ maxWidth: 820 }}>
      <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: colors.text, margin: 0, letterSpacing: tracking.tight, marginBottom: spacing.xl }}>
        Roles &amp; Permissions
      </h1>

      {error && (
        <div style={{
          background: colors.errorLight, color: colors.error,
          border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm,
          padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, marginBottom: spacing.lg,
        }}>{error}</div>
      )}

      <SectionCard
        title="Roles"
        subtitle="Custom roles group team members so folder permissions can be granted to many people at once."
        style={{ marginBottom: spacing.xl }}
      >
        {roles.length === 0 && (
          <div style={{ color: colors.textSecondary, fontSize: fontSize.sm, marginBottom: spacing.md }}>
            No roles yet. Create one below.
          </div>
        )}

        {roles.map(r => (
          <div key={r.roleId} style={{ borderTop: `1px solid ${colors.border}`, padding: `${spacing.sm} 0` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
              <FontAwesomeIcon icon={faUserGroup} style={{ width: 13, color: colors.textSecondary }} />
              <span style={{ fontWeight: 500, color: colors.text }}>{r.name}</span>
              <span style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>
                {r.memberCount} member{r.memberCount === 1 ? '' : 's'}
              </span>
              <div style={{ flex: 1 }} />
              <Button size="sm" variant="ghost" onClick={() => setExpandedRole(expandedRole === r.roleId ? null : r.roleId)}>
                {expandedRole === r.roleId ? 'Hide members' : 'Members'}
              </Button>
              <Button size="sm" variant="danger" icon={<FontAwesomeIcon icon={faTrash} style={{ width: 11 }} />} onClick={() => setDeleteTarget(r)}>
                Delete
              </Button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.xs, marginLeft: 21 }}>
              {TEAM_CAPABILITIES.map(cap => {
                const has = caps.some(g => g.roleId === r.roleId && g.capability === cap.code)
                return (
                  <label key={cap.code} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: fontSize.xs, color: has ? colors.text : colors.textSecondary, cursor: 'pointer' }}>
                    <input type="checkbox" checked={has} onChange={() => toggleCapability(r.roleId, cap.code, has)} />
                    {cap.label}
                  </label>
                )
              })}
            </div>
            {expandedRole === r.roleId && (
              <RoleMembersPanel role={r} teamUsers={teamUsers} onChanged={loadRoles} />
            )}
          </div>
        ))}

        <form onSubmit={handleCreate} style={{ display: 'flex', gap: spacing.sm, alignItems: 'flex-end', marginTop: spacing.lg, borderTop: `1px solid ${colors.border}`, paddingTop: spacing.lg }}>
          <div style={{ flex: 1 }}>
            <Input
              label="New role name"
              value={newRoleName}
              onChange={e => setNewRoleName(e.target.value)}
              placeholder="e.g. Contracts Team"
            />
          </div>
          <Button type="submit" loading={creating} disabled={!newRoleName.trim()} icon={<FontAwesomeIcon icon={faPlus} style={{ width: 12 }} />}>
            Create role
          </Button>
        </form>
      </SectionCard>

      <FolderPermissionsPanel roles={roles} folders={folders} onError={setError} />

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete role"
        message={deleteTarget
          ? `Delete "${deleteTarget.name}"? Its members and all folder permissions granted to it are removed. This cannot be undone.`
          : ''}
        confirmLabel="Delete role"
        danger
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
      {dialogs.element}
    </>
  )
}

// ── Members of one role ───────────────────────────────────────────────────
function RoleMembersPanel({ role, teamUsers, onChanged }: { role: Role; teamUsers: TeamMember[]; onChanged: () => void }) {
  const dialogs = useDialogs()
  const [members, setMembers] = useState<RoleMember[]>([])
  const [addUserId, setAddUserId] = useState<string>('')
  const [busy, setBusy] = useState(false)

  async function load() {
    try { setMembers(await rolesApiService.members(role.roleId)) } catch { /* non-fatal */ }
  }
  useEffect(() => { load() }, [role.roleId])

  const memberIds = useMemo(() => new Set(members.map(m => m.userId)), [members])
  const addable = teamUsers.filter(u => !memberIds.has(u.userId))

  async function add() {
    if (!addUserId) return
    setBusy(true)
    try {
      await rolesApiService.assign(addUserId, role.roleId)
      setAddUserId('')
      await load(); onChanged()
    } catch (err) {
      void dialogs.alert(err instanceof ApiError ? err.message : 'Failed to add member')
    } finally { setBusy(false) }
  }

  async function remove(userId: string) {
    setBusy(true)
    try {
      await rolesApiService.unassign(userId, role.roleId)
      await load(); onChanged()
    } catch (err) {
      void dialogs.alert(err instanceof ApiError ? err.message : 'Failed to remove member')
    } finally { setBusy(false) }
  }

  return (
    <>
    <div style={{ marginTop: spacing.sm, marginLeft: 21, paddingLeft: spacing.md, borderLeft: `2px solid ${colors.border}` }}>
      {members.length === 0 && (
        <div style={{ color: colors.textSecondary, fontSize: fontSize.sm, marginBottom: spacing.sm }}>No members.</div>
      )}
      {members.map(m => (
        <div key={m.userId} style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, padding: '3px 0' }}>
          <span style={{ fontSize: fontSize.sm, color: colors.text }}>{memberName(m)}</span>
          <div style={{ flex: 1 }} />
          <Button size="sm" variant="ghost" icon={<FontAwesomeIcon icon={faXmark} style={{ width: 12 }} />} disabled={busy} onClick={() => remove(m.userId)}>
            Remove
          </Button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'flex-end', marginTop: spacing.sm }}>
        <div style={{ flex: 1, maxWidth: 320 }}>
          <Select
            inputSize="sm"
            placeholder={addable.length ? 'Add a team member…' : 'All members added'}
            value={addUserId}
            isDisabled={busy || addable.length === 0}
            onChange={v => setAddUserId(String(v))}
            options={addable.map(u => ({ value: u.userId, label: memberName(u) }))}
          />
        </div>
        <Button size="sm" disabled={!addUserId || busy} onClick={add}>Add</Button>
      </div>
    </div>
      {dialogs.element}
    </>
  )
}

// ── Per-folder grants ──────────────────────────────────────────────────────
function FolderPermissionsPanel({ roles, folders, onError }: { roles: Role[]; folders: Folder[]; onError: (m: string) => void }) {
  const dialogs = useDialogs()
  const [folderId, setFolderId] = useState<string>('')
  const [grants, setGrants] = useState<FolderGrant[]>([])
  const [roleId, setRoleId] = useState<string>('')
  const [action, setAction] = useState<number>(PermissionAction.VIEW)
  const [busy, setBusy] = useState(false)

  const folderOptions = useMemo(
    () => folders.map(f => ({
      value: f.folderId,
      // path is "/{id}/{id}/"; depth-1 leading spaces give a tree feel in the flat <select>.
      label: `${'  '.repeat(Math.max(0, f.path.split('/').filter(Boolean).length - 1))}${f.name}`,
    })),
    [folders],
  )

  async function loadGrants(id: string) {
    if (!id) { setGrants([]); return }
    try { setGrants(await rolesApiService.folderGrants(id)) }
    catch (err) { onError(err instanceof ApiError ? err.message : 'Failed to load folder permissions') }
  }

  async function addGrant() {
    if (!folderId || !roleId) return
    setBusy(true)
    try {
      await rolesApiService.grant(folderId, roleId, action)
      await loadGrants(folderId)
    } catch (err) {
      void dialogs.alert(err instanceof ApiError ? err.message : 'Failed to grant permission')
    } finally { setBusy(false) }
  }

  async function revoke(g: FolderGrant) {
    setBusy(true)
    try {
      await rolesApiService.revoke(g.folderId, g.roleId, g.permissionActionLookupId)
      await loadGrants(folderId)
    } catch (err) {
      void dialogs.alert(err instanceof ApiError ? err.message : 'Failed to revoke permission')
    } finally { setBusy(false) }
  }

  return (
    <>
    <SectionCard
      title="Folder permissions"
      subtitle="Grant a role a permission on a folder. Grants apply to the folder and everything beneath it; the strongest grant wins."
    >
      <div style={{ maxWidth: 420, marginBottom: spacing.lg }}>
        <Select
          label="Folder"
          placeholder={folders.length ? 'Select a folder…' : 'No folders yet'}
          value={folderId}
          isDisabled={folders.length === 0}
          isSearchable
          onChange={v => { const id = String(v); setFolderId(id); loadGrants(id) }}
          options={folderOptions}
        />
      </div>

      {folderId && (
        <>
          {grants.length === 0 && (
            <div style={{ color: colors.textSecondary, fontSize: fontSize.sm, marginBottom: spacing.md }}>
              No permissions granted directly on this folder.
            </div>
          )}
          {grants.map(g => (
            <div key={g.folderRoleGrantId} style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, padding: '4px 0', borderTop: `1px solid ${colors.border}` }}>
              <FontAwesomeIcon icon={faUserGroup} style={{ width: 12, color: colors.textSecondary }} />
              <span style={{ fontSize: fontSize.sm, color: colors.text }}>{g.roleName}</span>
              <span style={{
                fontSize: fontSize.xs, fontWeight: 600, color: colors.primary,
                background: colors.primaryLight, borderRadius: radius.sm, padding: '2px 8px',
              }}>{ACTION_LABELS[g.permissionActionLookupId] ?? g.permissionActionLookupId}</span>
              <div style={{ flex: 1 }} />
              <Button size="sm" variant="ghost" icon={<FontAwesomeIcon icon={faXmark} style={{ width: 12 }} />} disabled={busy} onClick={() => revoke(g)}>
                Revoke
              </Button>
            </div>
          ))}

          <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'flex-end', marginTop: spacing.lg, borderTop: `1px solid ${colors.border}`, paddingTop: spacing.lg, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 200 }}>
              <Select
                label="Role"
                inputSize="sm"
                placeholder={roles.length ? 'Select a role…' : 'No roles yet'}
                value={roleId}
                isDisabled={roles.length === 0}
                onChange={v => setRoleId(String(v))}
                options={roles.map(r => ({ value: r.roleId, label: r.name }))}
              />
            </div>
            <div style={{ minWidth: 260 }}>
              <Select
                label="Permission"
                inputSize="sm"
                value={action}
                onChange={v => setAction(Number(v))}
                options={ACTION_OPTIONS}
              />
            </div>
            <Button size="sm" disabled={!roleId || busy} icon={<FontAwesomeIcon icon={faPlus} style={{ width: 12 }} />} onClick={addGrant}>
              Grant
            </Button>
          </div>
          <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: spacing.sm }}>
            <FontAwesomeIcon icon={faFolderTree} style={{ width: 11, marginRight: 4 }} />
            Granting a permission requires Manage on the selected folder.
          </div>
        </>
      )}
    </SectionCard>
      {dialogs.element}
    </>
  )
}
